/* eslint-disable no-inline-comments */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';
import AnimateHeight from 'react-animate-height';
import uniqueId from 'lodash/uniqueId';
import { useOutsideClickRef } from 'rooks';

import { DEFAULT_POPPER_OPTIONS } from './vertical-navigation-menu-item';

import { useVerticalNavigationContext } from '../vertical-navigation-context';

import { ReactComponent as HardwareIcKeyboardArrowDown24px } from '../../../assets/icons/hardware/ic_keyboard_arrow_right_24px.svg';

const cName = 'motif-vertical-navigation-submenu';
const cNamePrefix = `${cName}-`;
const portalClassName = `${cNamePrefix}portal`;

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} title
 * @prop {string} [className]
 * @prop {{expandToggle: React.ReactNode, title: React.ReactNode}} [icons]
 * @prop {() => void} [onClick]
 * @prop {() => void} [onMouseEnter]
 * @prop {() => void} [onMouseLeave]
 * @prop {() => void} [onOutsideClick]
 * @prop {boolean} [open]
 * @prop {boolean} [selected]
 */
/**
 * @param {Props} props
 */
const MotifVerticalNavigationSubmenu = ({
  children,
  className = '',
  icons = {},
  onClick = () => null,
  onMouseEnter = () => null,
  onMouseLeave = () => null,
  onOutsideClick = () => null,
  open = false,
  selected = false,
  title
}) => {
  const { collapse, rootClassName } = useVerticalNavigationContext();
  const buttonId = React.useRef(uniqueId()).current;
  const sectionId = React.useRef(`${cNamePrefix}${buttonId}`).current;
  const [referenceElement, setReferenceElement] = React.useState(null);
  const [popperElement, setPopperElement] = React.useState(null);
  const [ref] = useOutsideClickRef(onOutsideClick);
  const { styles, attributes } = usePopper(referenceElement, popperElement, DEFAULT_POPPER_OPTIONS);

  const classes = classNames({
    [cName]: true,
    [`${cNamePrefix}open`]: open,
    [`${cNamePrefix}selected`]: selected,
    [className]: true
  });

  const arrowClasses = classNames({
    [`${cNamePrefix}arrow`]: true,
    [`${cNamePrefix}arrow-open`]: open
  });

  const portalClasses = classNames({
    [portalClassName]: true,
    [className]: true,
    [rootClassName]: true
  });

  return (
    <React.Fragment>
      <button
        aria-controls={sectionId}
        aria-expanded={open}
        aria-label={title}
        className={classes}
        id={buttonId}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        title={title}
      >
        <div className="motif-vertical-navigation-submenu-iconcontainer" ref={setReferenceElement}>
          {icons.title}
        </div>
        {/* Portal shows floating submenu if menu is collapsed */}
        {collapse ? open && ReactDOM.createPortal(
          <div
            ref={(el) => {
              if (el) {
                setPopperElement(el);
                ref(el);
              }
            }}
            style={styles.popper}
            {...attributes.popper}
            className={portalClasses}
            data-testid={portalClassName}
          >
            <div className="motif-vertical-navigation-submenu-label">
              <div className="motif-vertical-navigation-submenu-float-title">{title}</div>
            </div>
            <div className="motif-vertical-navigation-submenu-children motif-vertical-navigation-scrolls">{children}</div>
          </div>,
          document.querySelector('body')
        ) : (
          <React.Fragment>
            <div className="motif-vertical-navigation-submenu-label">{title}</div>
            {icons.expandToggle || <HardwareIcKeyboardArrowDown24px className={arrowClasses} />}
          </React.Fragment>
        )}
      </button>
      {!collapse && (
        <AnimateHeight duration={150} height={open ? 'auto' : 0}>
          <nav
            id={sectionId}
            role="menubar"
            aria-labelledby={buttonId}
            className="motif-vertical-navigation-animateheight motif-vertical-navigation-scrolls"
          >
            {children}
          </nav>
        </AnimateHeight>
      )}
    </React.Fragment>
  );
};

MotifVerticalNavigationSubmenu.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)
  ]).isRequired,
  className: PropTypes.string,
  icons: PropTypes.shape({
    expandToggle: PropTypes.node,
    title: PropTypes.node
  }),
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onOutsideClick: PropTypes.func,
  open: PropTypes.bool,
  selected: PropTypes.bool,
  title: PropTypes.string.isRequired
};

export { MotifVerticalNavigationSubmenu };
